import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = `Forgot password — ${BRAND.name}`; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] px-5">
      <div className="w-full max-w-md brand-card p-8">
        <Link to="/login" data-testid="forgot-back-login" className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-brand font-semibold">
          ← Sign in
        </Link>
        {sent ? (
          <div className="mt-6 text-center" data-testid="forgot-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-heading font-bold text-2xl mt-5">Check your inbox</h1>
            <p className="mt-3 text-slate-600 text-[15px]">
              If an account exists for <span className="font-semibold text-slate-900">{email}</span>, a password-reset link has been sent. The link is valid for 30 minutes.
            </p>
            <p className="mt-4 text-xs text-slate-500">
              Didn't get the email? Check spam, or contact us on WhatsApp:{" "}
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="text-emerald-brand font-semibold">
                {BRAND.phoneIntl}
              </a>
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-bold text-3xl mt-4 leading-tight">Forgot your password?</h1>
            <p className="mt-2 text-sm text-slate-600">Enter your account email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Email</Label>
                <Input
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11"
                />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="btn-primary w-full text-sm">
                {loading ? "Sending…" : "Send reset link"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
